import React, { useEffect, useState, useRef } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Plus, LogOut, Package, ShoppingBag, Inbox, X, Image as ImageIcon, Edit3, Search, Star, Loader2, GripVertical, Tag, Layers, Sparkles, Images, BadgePercent } from "lucide-react";
import RichText from "../components/RichText";
import AdminPromotions from "../components/AdminPromotions";
import VariantsEditor from "../components/VariantsEditor";

const STORAGE = "aurea_admin_token";
const EMPTY = { name: "", brand: "", category: "", price: "", original_price: "", image: "", description: "", specs: "", stock: 10, featured: false, variants: [], warranty: "" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE) || "");
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("products");
  const [promoCount, setPromoCount] = useState("");
  const [loadErrors, setLoadErrors] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [newBrand, setNewBrand] = useState({ name: "", description: "", logo_url: "", sort_order: 100 });
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const brandLogoRef = useRef();
  const [categoriesList, setCategoriesList] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: "", icon: "Speaker", description: "", sort_order: 100 });
  const [packagesList, setPackagesList] = useState([]);
  const [galleryList, setGalleryList] = useState([]);
  const [pkgForm, setPkgForm] = useState({ id: "", name: "", tier: "Premium", price: "", image: "", description: "", highlights: "", sort_order: 100, consultation_only: false });
  const [galleryForm, setGalleryForm] = useState({ id: "", title: "", image: "", sort_order: 100 });
  const pkgImgRef = useRef();
  const galImgRef = useRef();
  const [pkgImgUploading, setPkgImgUploading] = useState(false);
  const [galImgUploading, setGalImgUploading] = useState(false);
  const [q, setQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const headers = { "X-Admin-Password": token };

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password: pw });
      localStorage.setItem(STORAGE, r.data.token);
      setToken(r.data.token);
      toast.success("Welcome to Batra CMS");
    } catch { toast.error("Invalid password"); }
  };
  const logout = () => { localStorage.removeItem(STORAGE); setToken(""); };

  const loadAll = async () => {
    const reqs = [
      ["Products", api.get("/products?limit=500"), setProducts],
      ["Orders", api.get("/orders", { headers }), setOrders],
      ["Inquiries", api.get("/inquiries", { headers }), setInquiries],
      ["Brands", api.get("/brands"), setBrandsList],
      ["Categories", api.get("/categories"), setCategoriesList],
      ["Packages", api.get("/packages"), setPackagesList],
      ["Gallery", api.get("/gallery"), setGalleryList],
    ];
    const results = await Promise.allSettled(reqs.map(([, p]) => p));
    if (results.some((r) => r.status === "rejected" && r.reason?.response?.status === 401)) {
      logout(); toast.error("Session expired"); return;
    }
    const failed = [];
    results.forEach((r, idx) => {
      const [label, , setter] = reqs[idx];
      if (r.status === "fulfilled") setter(r.value.data);
      else failed.push(label);
    });
    setLoadErrors(failed);
    if (failed.length) toast.error(`Couldn't load: ${failed.join(", ")}. Please refresh or try again.`);
  };
  useEffect(() => { if (token) loadAll(); }, [token]);
  useEffect(() => { if (token) api.get("/admin/promotions", { headers }).then((r) => setPromoCount(r.data.length)).catch(() => {}); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBrand = async (e) => {
    e.preventDefault();
    const name = newBrand.name.trim();
    if (!name) { toast.error("Brand name is required"); return; }
    try {
      await api.post("/admin/brands", { ...newBrand, name, sort_order: parseInt(newBrand.sort_order) || 100 }, { headers });
      toast.success(`${name} added`);
      setNewBrand({ name: "", description: "", logo_url: "", sort_order: 100 });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add brand"); }
  };

  const uploadBrandLogo = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBrandLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setNewBrand((prev) => ({ ...prev, logo_url: url }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBrandLogoUploading(false); }
  };

  const setBrandLogo = async (b, url) => {
    try {
      await api.put(`/admin/brands/${b.id}`, { name: b.name, description: b.description || "", logo_url: url, sort_order: b.sort_order || 100 }, { headers });
      toast.success(url ? "Logo saved" : "Logo removed");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const uploadExistingBrandLogo = async (b, f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      await setBrandLogo(b, url);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    const name = newCategory.name.trim();
    if (!name) { toast.error("Category name is required"); return; }
    try {
      await api.post("/admin/categories", { ...newCategory, name, sort_order: parseInt(newCategory.sort_order) || 100 }, { headers });
      toast.success(`${name} added`);
      setNewCategory({ name: "", icon: "Speaker", description: "", sort_order: 100 });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add category"); }
  };

  const delCategory = async (c) => {
    if (!confirm(`Remove '${c.name}' from Categories?\n\nNote: products with this category will still exist — you can re-categorise them under Products tab.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`, { headers });
      toast(`${c.name} removed`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ===== Gallery ("Installations that whisper luxury") =====
  const uploadGalleryImage = async (fl) => {
    if (!fl || !fl.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setGalImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", fl);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setGalleryForm((p) => ({ ...p, image: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setGalImgUploading(false); }
  };
  const submitGallery = async (e) => {
    e.preventDefault();
    if (!galleryForm.title || !galleryForm.image) { toast.error("Title and image are required"); return; }
    const payload = { title: galleryForm.title, image: galleryForm.image, sort_order: parseInt(galleryForm.sort_order) || 100 };
    try {
      if (galleryForm.id) {
        await api.put(`/admin/gallery/${galleryForm.id}`, payload, { headers });
        toast.success("Gallery item updated");
      } else {
        await api.post("/admin/gallery", payload, { headers });
        toast.success("Gallery item added");
      }
      setGalleryForm({ id: "", title: "", image: "", sort_order: 100 });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const editGallery = (g) => { setGalleryForm({ id: g.id, title: g.title, image: g.image, sort_order: g.sort_order || 100 }); };
  const delGallery = async (g) => {
    if (!confirm(`Remove '${g.title}' from the gallery?`)) return;
    try {
      await api.delete(`/admin/gallery/${g.id}`, { headers });
      toast(`${g.title} removed`);
      if (galleryForm.id === g.id) setGalleryForm({ id: "", title: "", image: "", sort_order: 100 });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ===== Packages (Home Theatre Packages) =====
  const uploadPkgImage = async (fl) => {
    if (!fl || !fl.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPkgImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", fl);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setPkgForm((p) => ({ ...p, image: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setPkgImgUploading(false); }
  };
  const submitPkg = async (e) => {
    e.preventDefault();
    if (!pkgForm.name || !pkgForm.image) { toast.error("Package name and image are required"); return; }
    const highlights = pkgForm.highlights.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = {
      name: pkgForm.name.trim(),
      tier: pkgForm.tier || "Premium",
      price: pkgForm.price ? parseFloat(pkgForm.price) : null,
      image: pkgForm.image,
      description: pkgForm.description || "",
      highlights,
      consultation_only: !!pkgForm.consultation_only,
      sort_order: parseInt(pkgForm.sort_order) || 100,
    };
    try {
      if (pkgForm.id) {
        await api.put(`/admin/packages/${pkgForm.id}`, payload, { headers });
        toast.success("Package updated");
      } else {
        await api.post("/admin/packages", payload, { headers });
        toast.success("Package added");
      }
      setPkgForm({ id: "", name: "", tier: "Premium", price: "", image: "", description: "", highlights: "", sort_order: 100, consultation_only: false });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const editPkg = (p) => {
    setPkgForm({
      id: p.id, name: p.name, tier: p.tier || "Premium",
      price: p.price ?? "", image: p.image || "", description: p.description || "",
      highlights: (p.highlights || []).join("\n"),
      sort_order: p.sort_order || 100,
      consultation_only: !!p.consultation_only,
    });
    window.scrollTo({ top: 300, behavior: "smooth" });
  };
  const delPkg = async (p) => {
    if (!confirm(`Remove package '${p.name}'?`)) return;
    try {
      await api.delete(`/admin/packages/${p.id}`, { headers });
      toast(`${p.name} removed`);
      if (pkgForm.id === p.id) setPkgForm({ id: "", name: "", tier: "Premium", price: "", image: "", description: "", highlights: "", sort_order: 100, consultation_only: false });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const delBrand = async (b) => {
    if (!confirm(`Remove '${b.name}' from Authorised Partners?`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`, { headers });
      toast(`${b.name} removed`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setDrawerOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...p, price: p.price?.toString() || "", original_price: p.original_price?.toString() || "", specs: (p.specs || []).join(" | "), images: p.images && p.images.length ? p.images : (p.image ? [p.image] : []) });
    setDrawerOpen(true);
  };
  const closeDrawer = () => { setDrawerOpen(false); setForm(EMPTY); setEditingId(null); };

  const uploadFile = async (f) => {
    if (!f) return null;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return null; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      return `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); return null; }
    finally { setUploading(false); }
  };

  const uploadImage = async (f) => {
    const url = await uploadFile(f);
    if (url) setForm((prev) => ({ ...prev, images: [...(prev.images || []), url], image: prev.image || url }));
  };

  const uploadMultiple = async (files) => {
    for (const f of files) await uploadImage(f);
  };

  const removeImage = (idx) => {
    setForm((prev) => {
      const next = (prev.images || []).filter((_, i) => i !== idx);
      return { ...prev, images: next, image: next[0] || "" };
    });
  };

  const setPrimary = (idx) => {
    setForm((prev) => {
      const next = [...(prev.images || [])];
      const [chosen] = next.splice(idx, 1);
      next.unshift(chosen);
      return { ...prev, images: next, image: chosen };
    });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/"));
    if (files.length) uploadMultiple(files);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const gallery = form.images && form.images.length ? form.images : (form.image ? [form.image] : []);
    if (gallery.length === 0) { toast.error("Please add at least one product image"); return; }
    setSaving(true);
    const payload = {
      name: form.name, brand: form.brand, category: form.category,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      image: gallery[0], images: gallery, description: form.description,
      specs: form.specs.split("|").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock) || 10, featured: !!form.featured,
      warranty: (form.warranty || "").trim(),
      variants: (form.variants || []).map((v) => ({ name: v.name.trim(), hex: v.hex.trim(), image: (v.image || "").trim(), sold_out: !!v.sold_out })),
    };
    try {
      if (editingId) await api.put(`/products/${editingId}`, payload, { headers });
      else await api.post("/products", payload, { headers });
      toast.success(editingId ? "Product updated" : "Product added");
      closeDrawer(); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const delProduct = async (id) => {
    if (!confirm("Delete this product permanently?")) return;
    await api.delete(`/products/${id}`, { headers });
    toast("Product deleted"); loadAll();
  };

  const uploadExcel = async (e) => {
    e.preventDefault(); if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/upload-excel", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} of ${r.data.total_rows} rows`);
      loadAll(); setFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const filtered = products.filter((p) => !q || `${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q.toLowerCase()));
  const uniqueBrands = [...new Set([...products.map((p) => p.brand), ...brandsList.map((b) => b.name)].filter(Boolean))].sort();
  const uniqueCats = [...new Set([...products.map((p) => p.category), ...categoriesList.map((c) => c.name)].filter(Boolean))].sort();

  // Count inquiries received in the last 7 days for the "new this week" badge
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const newInquiriesThisWeek = inquiries.filter((i) => {
    const ts = new Date(i.created_at || 0).getTime();
    return ts >= weekAgo;
  }).length;

  if (!token) {
    return (
      <div className="pt-40 pb-24 container-x max-w-md mx-auto" data-testid="admin-login">
        <div className="glass rounded-3xl p-10">
          <h1 className="font-display text-3xl mb-6">Admin · <em className="not-italic gold-text">Sign In</em></h1>
          <form onSubmit={login} className="space-y-3">
            <input type="password" placeholder="Admin password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="admin-pw" />
            <button type="submit" className="btn-gold w-full justify-center" data-testid="admin-login-btn">Enter</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 container-x" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="kicker mb-2">Control Panel</div>
          <h1 className="font-display text-4xl md:text-5xl">Batra <em className="not-italic gold-text">CMS.</em></h1>
        </div>
        <button onClick={logout} className="btn-ghost-outline text-xs px-5 py-2.5" data-testid="admin-logout"><LogOut className="w-3.5 h-3.5" /> Logout</button>
      </div>

      <div className="flex gap-3 mb-8 flex-wrap">
        {[["products", Package, "Products", products.length], ["orders", ShoppingBag, "Orders", orders.length], ["inquiries", Inbox, "Inquiries", inquiries.length], ["packages", Sparkles, "Packages", packagesList.length], ["gallery", Images, "Gallery", galleryList.length], ["brands", Tag, "Brands", brandsList.length], ["categories", Layers, "Categories", categoriesList.length], ["promotions", BadgePercent, "Offers", promoCount]].map(([id, I, l, n]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 transition ${tab === id ? "bg-gold text-black" : "glass hover:text-gold"}`} data-testid={`admin-tab-${id}`}>
            <I className="w-4 h-4" /> {l} <span className={`text-xs ${loadErrors.includes(l) ? "text-red-500 font-semibold" : tab === id ? "text-black/60" : "text-white/40"}`} data-testid={`admin-tab-count-${id}`}>{loadErrors.includes(l) ? "error" : n}</span>
          </button>
        ))}
      </div>

      {loadErrors.length > 0 && (
        <div className="mb-6 rounded-2xl px-5 py-4 text-sm flex items-center justify-between gap-4" style={{ background: "rgba(220,38,38,0.08)", color: "#991B1B", boxShadow: "inset 0 0 0 1px rgba(220,38,38,0.25)" }} data-testid="admin-load-error">
          <span>Couldn't load {loadErrors.join(", ")} from the server. The numbers shown may be incomplete.</span>
          <button onClick={loadAll} className="text-xs font-semibold underline" data-testid="admin-load-retry">Retry</button>
        </div>
      )}

      {tab === "products" && (
        <>
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full !pl-11" data-testid="admin-search" />
            </div>
            <div className="flex gap-3">
              <form onSubmit={uploadExcel} className="glass rounded-full flex items-center gap-2 pr-1 pl-4 py-1" data-testid="excel-form">
                <a href={`${API}/admin/excel-template`} className="text-xs text-gold hover:text-gold-soft whitespace-nowrap">Get template</a>
                <span className="text-white/20">·</span>
                <label className="text-xs text-white/60 hover:text-white cursor-pointer whitespace-nowrap">
                  <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" data-testid="excel-file" />
                  {file ? file.name.slice(0, 20) : "Choose Excel"}
                </label>
                {file && <button type="submit" className="bg-gold text-black text-xs font-medium px-4 py-2 rounded-full" data-testid="excel-upload">Import</button>}
              </form>
              <button onClick={openCreate} className="btn-gold text-sm" data-testid="new-product-btn"><Plus className="w-4 h-4" /> New Product</button>
            </div>
          </div>

          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left">
                    <th className="px-4 py-3 kicker">Product</th>
                    <th className="px-4 py-3 kicker">Brand</th>
                    <th className="px-4 py-3 kicker">Category</th>
                    <th className="px-4 py-3 kicker">Price</th>
                    <th className="px-4 py-3 kicker">Stock</th>
                    <th className="px-4 py-3 kicker">Featured</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02] transition" data-testid={`admin-product-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.image} className="w-11 h-11 rounded-lg object-cover" alt="" />
                          <span className="max-w-xs truncate">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white/60">{p.brand}</td>
                      <td className="px-4 py-3 text-white/60">{p.category}</td>
                      <td className="px-4 py-3">₹{p.price?.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-white/60">{p.stock}</td>
                      <td className="px-4 py-3">{p.featured && <Star className="w-4 h-4 text-gold fill-gold" />}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(p)} className="p-2 text-white/50 hover:text-gold" data-testid={`admin-edit-${p.id}`}><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => delProduct(p.id)} className="p-2 text-white/50 hover:text-red-400" data-testid={`admin-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-16 text-white/40" data-testid="admin-products-empty">{loadErrors.includes("Products") ? "Products couldn't be loaded. Use Retry above." : 'No products. Click "New Product" to add your first.'}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="glass rounded-2xl overflow-hidden" data-testid="admin-orders">
          <table className="w-full text-sm">
            <thead className="bg-white/5"><tr className="text-left">
              <th className="px-4 py-3 kicker">Session</th><th className="px-4 py-3 kicker">Customer</th><th className="px-4 py-3 kicker">Amount</th><th className="px-4 py-3 kicker">Status</th><th className="px-4 py-3 kicker">Date</th>
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.session_id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-xs font-mono text-white/50">{o.session_id?.slice(-10)}</td>
                  <td className="px-4 py-3">{o.full_name || "—"}<div className="text-xs text-white/40">{o.email}</div></td>
                  <td className="px-4 py-3">₹{(o.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-white/60"}`}>{o.payment_status}</span></td>
                  <td className="px-4 py-3 text-white/50 text-xs">{new Date(o.created_at).toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="text-center py-16 text-white/40">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-3" data-testid="admin-inquiries">
          <div className="text-sm text-white/60 mb-2">
            {inquiries.length === 0 ? "No inquiries yet" : `${inquiries.length} ${inquiries.length === 1 ? "inquiry" : "inquiries"} received${newInquiriesThisWeek > 0 ? ` · ${newInquiriesThisWeek} this week` : ""}`}
          </div>

          {inquiries.map((i) => (
            <div key={i.id} className="glass rounded-2xl p-5" data-testid={`inquiry-${i.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <span className="font-display text-lg">{i.name}</span>
                  <span className="text-xs text-white/40 ml-2">· {i.email}</span>
                </div>
                <span className="text-xs text-white/50 uppercase tracking-[0.18em]">{i.kind}</span>
              </div>
              {i.subject && <div className="text-sm mt-3 text-white/80">{i.subject}</div>}
              <p className="text-sm text-white/60 mt-2 whitespace-pre-wrap leading-relaxed">{i.message}</p>
              {i.details?.image_url && (
                <a href={i.details.image_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-gold hover:underline">View attached photo →</a>
              )}
              <div className="text-xs text-white/40 mt-3">
                {new Date(i.created_at).toLocaleString('en-IN')} · {i.phone || "no phone"}
              </div>
            </div>
          ))}
          {inquiries.length === 0 && <div className="glass rounded-2xl py-16 text-center text-white/40">No inquiries yet</div>}
        </div>
      )}

      {tab === "brands" && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-brands">
          <form onSubmit={addBrand} className="glass rounded-2xl p-6 space-y-4 lg:col-span-1 h-fit">
            <div>
              <div className="kicker mb-2">Add a Brand</div>
              <div className="text-xs text-white/50 leading-relaxed">Brands appear on the Home "Authorised Partners" grid and the /brands page — sorted by <span className="text-white/70">Sort Order</span> ascending.</div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Brand name *</label>
              <input required placeholder="e.g. Bowers & Wilkins" value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} data-testid="new-brand-name" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Description (shown on /brands page)</label>
              <textarea rows="3" placeholder="Studio-grade fidelity, Abbey Road heritage." value={newBrand.description} onChange={(e) => setNewBrand({ ...newBrand, description: e.target.value })} data-testid="new-brand-desc"></textarea>
            </div>

            <div>
              <label className="text-xs text-white/50 block mb-1.5">Logo (optional — PNG with transparent bg works best)</label>
              <input ref={brandLogoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBrandLogo(e.target.files?.[0])} data-testid="new-brand-logo-file" />
              {newBrand.logo_url ? (
                <div className="flex items-center gap-3 rounded-xl p-3 border border-white/10">
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center bg-black/5">
                    <img src={newBrand.logo_url} alt="logo" className="max-w-full max-h-full object-contain" style={{ mixBlendMode: "multiply" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/60 truncate">Logo attached</div>
                    <div className="flex gap-2 mt-1">
                      <button type="button" onClick={() => brandLogoRef.current?.click()} className="text-[10px] uppercase tracking-wider text-gold hover:brightness-125" data-testid="new-brand-logo-replace">Replace</button>
                      <button type="button" onClick={() => setNewBrand({ ...newBrand, logo_url: "" })} className="text-[10px] uppercase tracking-wider text-red-400 hover:brightness-125">Remove</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => brandLogoRef.current?.click()} className="w-full py-4 rounded-xl border border-dashed border-white/20 hover:border-gold/50 transition text-xs text-white/60 flex items-center justify-center gap-2" data-testid="new-brand-logo-upload">
                  {brandLogoUploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Upload logo</>}
                </button>
              )}
            </div>

            <div>
              <label className="text-xs text-white/50 block mb-1.5">Sort order (lower = earlier)</label>
              <input type="number" placeholder="100" value={newBrand.sort_order} onChange={(e) => setNewBrand({ ...newBrand, sort_order: e.target.value })} data-testid="new-brand-order" />
            </div>
            <button type="submit" className="btn-gold w-full justify-center text-sm" data-testid="add-brand-btn"><Plus className="w-4 h-4" /> Add Brand</button>
          </form>

          <div className="glass rounded-2xl overflow-hidden lg:col-span-2">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="kicker">Current Brands ({brandsList.length})</div>
            </div>
            <div className="divide-y divide-white/5">
              {brandsList.map((b) => (
                <div key={b.id} className="px-5 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition" data-testid={`admin-brand-${b.name}`}>
                  <div className="text-[10px] font-mono text-white/40 mt-4 w-8">{b.sort_order}</div>
                  <label className={`w-14 h-14 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer border transition group/logo ${b.logo_url ? "border-white/10 bg-black/5" : "border-dashed border-white/20 hover:border-gold/60"}`} title={b.logo_url ? "Click to replace logo" : "Click to upload logo"}>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadExistingBrandLogo(b, e.target.files?.[0])} data-testid={`brand-logo-input-${b.name}`} />
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name} className="max-w-full max-h-full object-contain" style={{ mixBlendMode: "multiply" }} />
                    ) : (
                      <Upload className="w-4 h-4 text-white/40 group-hover/logo:text-gold" />
                    )}
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl">{b.name}</div>
                    {b.description && <div className="text-xs text-white/50 mt-1 leading-relaxed">{b.description}</div>}
                    {b.logo_url && (
                      <button onClick={() => setBrandLogo(b, "")} className="text-[10px] uppercase tracking-wider text-white/40 hover:text-red-400 mt-2" data-testid={`brand-logo-remove-${b.name}`}>Remove logo</button>
                    )}
                  </div>
                  <button onClick={() => delBrand(b)} className="p-2 text-white/40 hover:text-red-400 shrink-0" data-testid={`del-brand-${b.name}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {brandsList.length === 0 && <div className="py-16 text-center text-white/40">No brands yet — add your first on the left.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "promotions" && <AdminPromotions headers={headers} products={products} categories={categoriesList} onCount={setPromoCount} />}

      {tab === "categories" && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-categories">
          <form onSubmit={addCategory} className="glass rounded-2xl p-6 space-y-4 lg:col-span-1 h-fit">
            <div>
              <div className="kicker mb-2">Add a Category</div>
              <div className="text-xs text-white/50 leading-relaxed">Categories appear on the Home "Featured Categories" grid — sorted by <span className="text-white/70">Sort Order</span> ascending.</div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Category name *</label>
              <input required placeholder="e.g. Turntables" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} data-testid="new-cat-name" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Icon</label>
              <select value={newCategory.icon} onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })} data-testid="new-cat-icon">
                <option value="Speaker">Speaker</option>
                <option value="Waves">Waves (Atmos)</option>
                <option value="Disc3">Disc (Hi-Fi)</option>
                <option value="Volume2">Volume (Soundbar)</option>
                <option value="Cpu">Cpu (Receiver)</option>
                <option value="Radio">Radio (Sub)</option>
                <option value="Headphones">Headphones</option>
                <option value="Tv">Tv (Projector)</option>
                <option value="Sparkles">Sparkles</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Description (optional)</label>
              <textarea rows="2" placeholder="Short line shown to visitors" value={newCategory.description} onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })} data-testid="new-cat-desc"></textarea>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Sort order (lower = earlier)</label>
              <input type="number" placeholder="100" value={newCategory.sort_order} onChange={(e) => setNewCategory({ ...newCategory, sort_order: e.target.value })} data-testid="new-cat-order" />
            </div>
            <button type="submit" className="btn-gold w-full justify-center text-sm" data-testid="add-cat-btn"><Plus className="w-4 h-4" /> Add Category</button>
          </form>

          <div className="glass rounded-2xl overflow-hidden lg:col-span-2">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="kicker">Current Categories ({categoriesList.length})</div>
            </div>
            <div className="divide-y divide-white/5">
              {categoriesList.map((c) => (
                <div key={c.id} className="px-5 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition" data-testid={`admin-cat-${c.name}`}>
                  <div className="text-[10px] font-mono text-white/40 mt-1.5 w-8">{c.sort_order}</div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(138,106,15,0.08)", border: "1px solid rgba(138,106,15,0.2)" }}>
                    <span className="text-[10px] font-mono text-gold uppercase tracking-wider">{c.icon.slice(0, 3)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl">{c.name}</div>
                    {c.description && <div className="text-xs text-white/50 mt-1 leading-relaxed">{c.description}</div>}
                  </div>
                  <button onClick={() => delCategory(c)} className="p-2 text-white/40 hover:text-red-400 shrink-0" data-testid={`del-cat-${c.name}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {categoriesList.length === 0 && <div className="py-16 text-center text-white/40">No categories yet — add your first on the left.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div className="grid lg:grid-cols-5 gap-6" data-testid="admin-packages">
          <form onSubmit={submitPkg} className="glass rounded-2xl p-6 space-y-4 lg:col-span-2 h-fit">
            <div>
              <div className="kicker mb-2">{pkgForm.id ? "Edit Package" : "Add a Package"}</div>
              <div className="text-xs text-white/50 leading-relaxed">Appears in the "Home Theatre Packages" section on Home + `/packages` page. Sorted by <span className="text-white/70">Sort Order</span>.</div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Package name *</label>
              <input required placeholder="e.g. Atmos 7.2.4 Reference" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} data-testid="pkg-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/50 block mb-1.5">Tier</label>
                <select value={pkgForm.tier} onChange={(e) => setPkgForm({ ...pkgForm, tier: e.target.value })} data-testid="pkg-tier">
                  <option>Starter</option>
                  <option>Premium</option>
                  <option>Atmos</option>
                  <option>Luxury</option>
                  <option>Reference</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1.5">Sort order</label>
                <input type="number" placeholder="100" value={pkgForm.sort_order} onChange={(e) => setPkgForm({ ...pkgForm, sort_order: e.target.value })} data-testid="pkg-order" />
              </div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Price (₹) — leave blank for "On consultation"</label>
              <input type="number" placeholder="e.g. 499000" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} data-testid="pkg-price" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Image *</label>
              <input ref={pkgImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPkgImage(e.target.files?.[0])} data-testid="pkg-img-file" />
              {pkgForm.image ? (
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <img src={pkgForm.image} alt="pkg" className="w-full h-40 object-cover" />
                  <div className="p-2 flex justify-between items-center">
                    <div className="text-[10px] text-white/50 truncate mr-2">Image attached</div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => pkgImgRef.current?.click()} className="text-[10px] uppercase tracking-wider text-gold" data-testid="pkg-img-replace">Replace</button>
                      <button type="button" onClick={() => setPkgForm({ ...pkgForm, image: "" })} className="text-[10px] uppercase tracking-wider text-red-400">Remove</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => pkgImgRef.current?.click()} className="w-full py-8 rounded-xl border-2 border-dashed border-white/20 hover:border-gold/50 transition text-xs text-white/60 flex flex-col items-center gap-2" data-testid="pkg-img-upload">
                  {pkgImgUploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Uploading…</> : <><ImageIcon className="w-6 h-6 text-gold" />Click to upload package image</>}
                </button>
              )}
              <input type="text" placeholder="Or paste image URL" value={pkgForm.image} onChange={(e) => setPkgForm({ ...pkgForm, image: e.target.value })} className="mt-2 text-xs" data-testid="pkg-img-url" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Highlights (one per line)</label>
              <textarea rows="4" placeholder="7.2.4 Dolby Atmos&#10;4K laser projector&#10;Acoustic treatment&#10;Certified installation" value={pkgForm.highlights} onChange={(e) => setPkgForm({ ...pkgForm, highlights: e.target.value })} data-testid="pkg-highlights"></textarea>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Description (optional, appears on package detail)</label>
              <textarea rows="3" value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} data-testid="pkg-desc"></textarea>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input type="checkbox" checked={pkgForm.consultation_only} onChange={(e) => setPkgForm({ ...pkgForm, consultation_only: e.target.checked })} data-testid="pkg-consult-only" />
              Consultation only (hide price, show "Enquire" instead)
            </label>
            <div className="flex gap-2">
              {pkgForm.id && (
                <button type="button" onClick={() => setPkgForm({ id: "", name: "", tier: "Premium", price: "", image: "", description: "", highlights: "", sort_order: 100, consultation_only: false })} className="btn-ghost-outline text-sm flex-1 justify-center">Cancel</button>
              )}
              <button type="submit" className="btn-gold flex-1 justify-center text-sm" data-testid="pkg-submit"><Plus className="w-4 h-4" /> {pkgForm.id ? "Save Changes" : "Add Package"}</button>
            </div>
          </form>

          <div className="glass rounded-2xl overflow-hidden lg:col-span-3">
            <div className="px-5 py-4 border-b border-white/5 kicker">Current Packages ({packagesList.length})</div>
            <div className="divide-y divide-white/5">
              {packagesList.map((p) => (
                <div key={p.id} className="px-5 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition" data-testid={`admin-pkg-${p.id}`}>
                  <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0" style={{ background: "rgba(15,12,8,0.06)" }}>
                    {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-gold">{p.tier}</span>
                      <span className="font-mono text-[10px] text-white/30">·</span>
                      <span className="font-mono text-[10px] text-white/40">#{p.sort_order || 0}</span>
                    </div>
                    <div className="font-display text-xl">{p.name}</div>
                    <div className="text-xs text-white/50 mt-1">
                      {p.price ? `₹${p.price.toLocaleString('en-IN')}` : (p.consultation_only ? "On consultation" : "No price set")}
                      {p.highlights?.length ? ` · ${p.highlights.length} highlights` : ""}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => editPkg(p)} className="p-2 text-white/50 hover:text-gold" title="Edit" data-testid={`edit-pkg-${p.id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => delPkg(p)} className="p-2 text-white/40 hover:text-red-400" data-testid={`del-pkg-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {packagesList.length === 0 && <div className="py-16 text-center text-white/40">No packages yet — add your first on the left.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "gallery" && (
        <div className="grid lg:grid-cols-5 gap-6" data-testid="admin-gallery">
          <form onSubmit={submitGallery} className="glass rounded-2xl p-6 space-y-4 lg:col-span-2 h-fit">
            <div>
              <div className="kicker mb-2">{galleryForm.id ? "Edit Photo" : "Add a Photo"}</div>
              <div className="text-xs text-white/50 leading-relaxed">Appears in the "Installations that whisper luxury" gallery on Home. Sort order controls display sequence.</div>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Title / caption *</label>
              <input required placeholder="e.g. Private Atmos Theatre — Mussoorie" value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} data-testid="gal-title" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Sort order</label>
              <input type="number" placeholder="100" value={galleryForm.sort_order} onChange={(e) => setGalleryForm({ ...galleryForm, sort_order: e.target.value })} data-testid="gal-order" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1.5">Image *</label>
              <input ref={galImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadGalleryImage(e.target.files?.[0])} data-testid="gal-img-file" />
              {galleryForm.image ? (
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <img src={galleryForm.image} alt="preview" className="w-full h-44 object-cover" />
                  <div className="p-2 flex justify-between items-center">
                    <div className="text-[10px] text-white/50 truncate mr-2">Image attached</div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => galImgRef.current?.click()} className="text-[10px] uppercase tracking-wider text-gold" data-testid="gal-img-replace">Replace</button>
                      <button type="button" onClick={() => setGalleryForm({ ...galleryForm, image: "" })} className="text-[10px] uppercase tracking-wider text-red-400">Remove</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => galImgRef.current?.click()} className="w-full py-8 rounded-xl border-2 border-dashed border-white/20 hover:border-gold/50 transition text-xs text-white/60 flex flex-col items-center gap-2" data-testid="gal-img-upload">
                  {galImgUploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Uploading…</> : <><ImageIcon className="w-6 h-6 text-gold" />Click to upload photo</>}
                </button>
              )}
              <input type="text" placeholder="Or paste image URL" value={galleryForm.image} onChange={(e) => setGalleryForm({ ...galleryForm, image: e.target.value })} className="mt-2 text-xs" data-testid="gal-img-url" />
            </div>
            <div className="flex gap-2">
              {galleryForm.id && <button type="button" onClick={() => setGalleryForm({ id: "", title: "", image: "", sort_order: 100 })} className="btn-ghost-outline text-sm flex-1 justify-center">Cancel</button>}
              <button type="submit" className="btn-gold flex-1 justify-center text-sm" data-testid="gal-submit"><Plus className="w-4 h-4" /> {galleryForm.id ? "Save Changes" : "Add Photo"}</button>
            </div>
          </form>

          <div className="glass rounded-2xl overflow-hidden lg:col-span-3">
            <div className="px-5 py-4 border-b border-white/5 kicker">Gallery Photos ({galleryList.length})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4">
              {galleryList.map((g) => (
                <div key={g.id} className="rounded-xl overflow-hidden relative group" data-testid={`admin-gallery-${g.id}`}>
                  <div className="aspect-[4/3]"><img src={g.image} alt={g.title} className="w-full h-full object-cover" /></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 opacity-0 group-hover:opacity-100 transition flex flex-col justify-end p-3 gap-2">
                    <div className="text-xs text-white truncate">{g.title}</div>
                    <div className="flex gap-2">
                      <button onClick={() => editGallery(g)} className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-1" data-testid={`edit-gal-${g.id}`}><Edit3 className="w-3 h-3" /> Edit</button>
                      <button onClick={() => delGallery(g)} className="text-[10px] uppercase tracking-wider text-red-400 flex items-center gap-1" data-testid={`del-gal-${g.id}`}><Trash2 className="w-3 h-3" /> Remove</button>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 text-[9px] font-mono text-white/70 px-1.5 py-0.5 rounded bg-black/50">#{g.sort_order || 0}</div>
                </div>
              ))}
              {galleryList.length === 0 && <div className="col-span-full py-16 text-center text-white/40">No gallery photos yet.</div>}
            </div>
          </div>
        </div>
      )}



      {/* PRODUCT DRAWER */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[100] flex" data-testid="product-drawer">
          <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={closeDrawer}></div>
          <div className="w-full max-w-2xl bg-charcoal-900 border-l border-white/10 overflow-y-auto animate-fade-in-up">
            <div className="sticky top-0 z-10 glass-strong border-b border-white/5 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="kicker mb-1">{editingId ? "Edit Product" : "New Product"}</div>
                <h2 className="font-display text-2xl">{form.name || "Untitled Product"}</h2>
              </div>
              <button onClick={closeDrawer} className="p-2 hover:text-gold" data-testid="drawer-close"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={saveProduct} className="p-8 space-y-8">
              {/* MEDIA */}
              <section>
                <div className="kicker mb-3">Product Images (first = main)</div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                  className={`relative rounded-2xl border-2 border-dashed transition-all cursor-pointer aspect-[16/6] overflow-hidden ${dragOver ? "border-gold bg-gold/5" : "border-white/15 hover:border-gold/40"}`}
                  data-testid="image-dropzone"
                >
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadMultiple(Array.from(e.target.files || []))} data-testid="image-input" />
                  {uploading && <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50">
                    <div className="w-14 h-14 rounded-full glass flex items-center justify-center text-gold mb-3"><ImageIcon className="w-6 h-6" /></div>
                    <div className="text-sm">Drag & drop images (multiple), or click to browse</div>
                    <div className="text-xs text-white/40 mt-1">JPG, PNG, WEBP · max 8MB each</div>
                  </div>
                </div>
                {form.images && form.images.length > 0 && (
                  <div className="mt-4 grid grid-cols-4 gap-3" data-testid="gallery-thumbs">
                    {form.images.map((url, i) => (
                      <div key={i} className={`relative group aspect-square rounded-xl overflow-hidden border-2 ${i === 0 ? "border-gold" : "border-white/10"}`} data-testid={`gallery-thumb-${i}`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {i === 0 && <span className="absolute top-1 left-1 bg-gold text-black text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium">Main</span>}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-2">
                          {i !== 0 && <button type="button" onClick={() => setPrimary(i)} className="text-[10px] px-2 py-1 rounded bg-gold text-black" data-testid={`primary-${i}`}>Set as main</button>}
                          <button type="button" onClick={() => removeImage(i)} className="text-[10px] px-2 py-1 rounded bg-red-500/80 text-white" data-testid={`remove-img-${i}`}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <input placeholder="Or paste an image URL and press Enter" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { e.preventDefault(); const url = e.target.value.trim(); setForm((prev) => ({ ...prev, images: [...(prev.images || []), url], image: prev.image || url })); e.target.value = ""; } }} className="mt-3 !text-xs" data-testid="drawer-image-url" />
              </section>

              {/* BASIC INFO */}
              <section className="space-y-3">
                <div className="kicker">Basic Info</div>
                <div>
                  <label className="text-xs text-white/50 block mb-1.5">Product Name *</label>
                  <input required placeholder="e.g. Klipsch RP-8000F II" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="drawer-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/50 block mb-1.5">Brand *</label>
                    <input required list="brand-list" placeholder="Klipsch" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="drawer-brand" />
                    <datalist id="brand-list">{uniqueBrands.map((b) => <option key={b} value={b} />)}</datalist>
                  </div>
                  <div>
                    <label className="text-xs text-white/50 block mb-1.5">Category *</label>
                    <input required list="cat-list" placeholder="Tower Speakers" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="drawer-category" />
                    <datalist id="cat-list">{uniqueCats.map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1.5">Description</label>
                  <RichText value={form.description} onChange={(html) => setForm({ ...form, description: html })} testId="drawer-description" />
                </div>
              </section>

              {/* PRICING & STOCK */}
              <section className="space-y-3">
                <div className="kicker">Pricing & Inventory</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-white/50 block mb-1.5">Price (₹) *</label>
                    <input required type="number" step="0.01" placeholder="1499" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="drawer-price" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 block mb-1.5">Original (₹)</label>
                    <input type="number" step="0.01" placeholder="1699" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} data-testid="drawer-original-price" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 block mb-1.5">Stock</label>
                    <input type="number" placeholder="10" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="drawer-stock" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1.5">Warranty</label>
                  <input maxLength={80} placeholder="e.g. 2 Year Manufacturer Warranty" value={form.warranty || ""} onChange={(e) => setForm({ ...form, warranty: e.target.value })} className="w-full" data-testid="drawer-warranty" />
                  <div className="text-[11px] text-white/40 mt-1">Leave empty to hide it on the product page.</div>
                </div>
              </section>

              <VariantsEditor variants={form.variants} onChange={(variants) => setForm((prev) => ({ ...prev, variants }))} upload={uploadFile} />

              {/* SPECS */}
              <section>
                <div className="kicker mb-3">Specifications</div>
                <label className="text-xs text-white/50 block mb-1.5">Enter each spec separated by | (pipe)</label>
                <input placeholder='e.g. Tractrix Horn | Dual 8" Woofer | 150W | Ebony' value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} data-testid="drawer-specs" />
                {form.specs && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.specs.split("|").map((s) => s.trim()).filter(Boolean).map((s, i) => (
                      <span key={i} className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5">{s}</span>
                    ))}
                  </div>
                )}
              </section>

              {/* VISIBILITY */}
              <section>
                <div className="kicker mb-3">Visibility</div>
                <label className="flex items-center gap-3 glass rounded-xl p-4 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" data-testid="drawer-featured" />
                  <div>
                    <div className="text-sm font-medium">Featured on Homepage</div>
                    <div className="text-xs text-white/50">Show this product in the "Handpicked reference gear" section.</div>
                  </div>
                </label>
              </section>

              {/* ACTIONS */}
              <div className="sticky bottom-0 -mx-8 px-8 py-5 bg-charcoal-900/90 backdrop-blur-xl border-t border-white/10 flex justify-end gap-3">
                <button type="button" onClick={closeDrawer} className="btn-ghost-outline text-sm px-5 py-2.5">Cancel</button>
                <button type="submit" disabled={saving} className="btn-gold text-sm" data-testid="drawer-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {editingId ? "Save changes" : "Publish product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
